package domain.levels

import utils.toStringOneDecimal

/**
 * Defines what a player must accomplish to complete the current room (level).
 *
 * The objective type and target are entirely card-driven. If no card overrides
 * the objective, the default is [Lines] with a target of 10.
 */
sealed class RoomObjective {

    /** Human-readable label shown in the HUD (e.g. "LINES", "SCORE", "SPINS"). */
    abstract val label: String

    /** The target value the player must reach. */
    abstract val target: Double

    /** Returns true when [current] satisfies this objective. */
    abstract fun isMet(current: Double): Boolean

    /** Formats [current] for display in the HUD progress widget. */
    open fun formatProgress(current: Double): String =
        "${current.toLong()} / ${target.toLong()}"

    // ── Concrete types ────────────────────────────────────────────────────────

    /** Classic: clear [target] lines. */
    data class Lines(override val target: Double = 10.0) : RoomObjective() {
        override val label get() = "LINES"
        override fun isMet(current: Double) = current >= target
    }

    /** Score: accumulate [target] points this room. */
    data class Score(override val target: Double = 5000.0) : RoomObjective() {
        override val label get() = "SCORE"
        override fun isMet(current: Double) = current >= target
        override fun formatProgress(current: Double): String {
            fun fmt(v: Double) = when {
                v >= 1_000_000 -> "${(v / 1_000_000).toStringOneDecimal()}M"
                v >= 1_000     -> "${(v / 1_000).toStringOneDecimal()}K"
                else           -> "${v.toLong()}"
            }
            return "${fmt(current)} / ${fmt(target)}"
        }
    }

    /**
     * Difficult clears: accumulate [target] "difficult" line clears.
     * A difficult clear is any Tetris (4 lines) or any spin clear.
     */
    data class DifficultClears(override val target: Double = 3.0) : RoomObjective() {
        override val label get() = "DIFF. CLEARS"
        override fun isMet(current: Double) = current >= target
    }

    /**
     * Spins: land [target] spin clears (mini or full).
     */
    data class Spins(override val target: Double = 2.0) : RoomObjective() {
        override val label get() = "SPINS"
        override fun isMet(current: Double) = current >= target
    }

    /**
     * Combos: reach a combo of at least [target] in a single chain.
     */
    data class MaxCombo(override val target: Double = 4.0) : RoomObjective() {
        override val label get() = "MAX COMBO"
        override fun isMet(current: Double) = current >= target
        override fun formatProgress(current: Double) =
            "x${current.toLong()} / x${target.toLong()}"
    }

    /**
     * Survival: stay alive for [target] pieces without topping out.
     */
    data class PieceSurvival(override val target: Double = 50.0) : RoomObjective() {
        override val label get() = "SURVIVE"
        override fun isMet(current: Double) = current >= target
        override fun formatProgress(current: Double) =
            "${current.toLong()} pieces / ${target.toLong()}"
    }
}
